import { useLayoutEffect } from 'react';

import { EyeInvisibleOutlined, EyeOutlined, FormOutlined } from '@ant-design/icons';
import { Card, Input, Switch, Tag, Button } from 'antd';
import locale from 'antd/es/date-picker/locale/ru_RU';
import { parse } from 'date-fns';
import { useNavigate, useParams } from 'react-router';

import { useGetAdmissionSurveyQuery } from '@/api/endpoints/schools/schoolSelectionPanel';
import { type IApiError } from '@/api/types';
import { DataSection, DatePicker, FetchDataWrapper } from '@/shared/components';
import { DATE_FORMATTERS, ROUTES } from '@/shared/consts';

import { ESelectionPanelTabs } from '../../../_components/SelectionPanelHeader/consts';
import { SelectionPanelHeader } from '../../../_components/SelectionPanelHeader/SelectionPanelHeader';
import { QuestionsList } from '../../_components/QuestionsList/QuestionsList';

import styles from './SurveyViewPage.module.scss';

export const SurveyViewPage = () => {
  const { id: schoolId } = useParams();

  const {
    data: surveyData,
    isLoading: isSurveyLoading,
    isError: isSurveyFetchError,
    error,
  } = useGetAdmissionSurveyQuery({ schoolId: Number(schoolId) });

  const navigate = useNavigate();

  useLayoutEffect(() => {
    if ((error as IApiError)?.status === 404) {
      navigate(
        `/${ROUTES.PUBLIC_PAGES}/${ROUTES.SCHOOLS}/${schoolId}/${ROUTES.SELECTION_PANEL}/${ROUTES.ADMISSION_SURVEY}/${ROUTES.CREATE}`,
        { replace: true },
      );
    }
  }, [error]);

  return (
    <FetchDataWrapper
      isLoading={isSurveyLoading}
      isError={isSurveyFetchError}
    >
      {surveyData &&
        (() => {
          const { start_date: startDate, end_date: endDate, active: isActive, title: surveyName } = surveyData;

          return (
            <div className={styles.wrapper}>
              <SelectionPanelHeader currentTab={ESelectionPanelTabs.AdmissionSurvey} />
              <div className={styles['header-wrapper']}>
                <div className={styles['page-info-wrapper']}>
                  <p className={styles.title}>Просмотр опроса</p>
                </div>
                <div className={styles['header-action-wrapper']}>
                  <Tag
                    className={styles['status-tag']}
                    icon={isActive ? <EyeOutlined /> : <EyeInvisibleOutlined />}
                    color={isActive ? 'green' : 'default'}
                  >
                    {isActive ? 'Активен' : 'Не активен'}
                  </Tag>
                  <Button
                    icon={<FormOutlined />}
                    onClick={() =>
                      navigate(
                        `/${ROUTES.PUBLIC_PAGES}/${ROUTES.SCHOOLS}/${schoolId}/${ROUTES.SELECTION_PANEL}/${ROUTES.ADMISSION_SURVEY}/${ROUTES.EDIT}`,
                      )
                    }
                  >
                    Редактировать
                  </Button>
                </div>
              </div>
              <Card>
                <div className={styles['main-info-card-wrapper']}>
                  <p className={styles['card-title']}>Основная информация</p>
                  <DataSection
                    titleText="Название опроса"
                    isTitleRequired
                  >
                    <Input
                      placeholder="Например: Заявка на обучение 2026"
                      disabled
                      value={surveyName}
                    />
                  </DataSection>
                  <div className={styles['survey-switcher-wrapper']}>
                    <div className={styles['survey-switcher-info-wrapper']}>
                      <p className={styles['survey-switcher-title']}>Опрос активен</p>
                      <p className={styles['survey-switcher-description']}>Разрешить прием заявок через этот опрос</p>
                    </div>
                    <Switch
                      disabled
                      value={isActive}
                    />
                  </div>
                  <div className={styles['half-width-sections-wrapper']}>
                    <DataSection
                      titleText="Дата начала"
                      isTitleRequired
                      wrapperClassName={styles['half-width-section']}
                    >
                      <DatePicker
                        disabled
                        format={DATE_FORMATTERS.DAY_MONTH_FULL_YEAR}
                        placement="topRight"
                        value={parse(startDate, DATE_FORMATTERS.FULL_YEAR_MONTH_DAY, new Date())}
                        locale={locale}
                      />
                    </DataSection>
                    <DataSection
                      titleText="Дата окончания"
                      isTitleRequired
                      wrapperClassName={styles['half-width-section']}
                    >
                      <DatePicker
                        disabled
                        format={DATE_FORMATTERS.DAY_MONTH_FULL_YEAR}
                        placement="topRight"
                        value={parse(endDate, DATE_FORMATTERS.FULL_YEAR_MONTH_DAY, new Date())}
                        locale={locale}
                      />
                    </DataSection>
                  </div>
                </div>
              </Card>
              <Card>
                <div className={styles['main-info-card-wrapper']}>
                  <p className={styles['card-title']}>Поля опроса</p>
                  <QuestionsList isEditMode={false} />
                </div>
              </Card>
            </div>
          );
        })()}
    </FetchDataWrapper>
  );
};
